import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../hooks/redux';
import { showToast } from '../features/ui/uiSlice';
import api from '../utils/axiosConfig';
import {
    BuildingOfficeIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    GlobeAltIcon,
    ClipboardDocumentListIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    EyeIcon,
    ChatBubbleLeftRightIcon,
    FunnelIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface CompanyInquiry {
    _id: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    status: 'pending' | 'contacted' | 'in-progress' | 'approved' | 'rejected';
    adminNotes?: string;
    contactedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    contactedAt?: string;
    createdAt: string;
    updatedAt: string;
}

const AdminCompanyInquiryManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const [inquiries, setInquiries] = useState<CompanyInquiry[]>([]);
    const [selectedInquiry, setSelectedInquiry] = useState<CompanyInquiry | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactNotes, setContactNotes] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDetails, setShowDetails] = useState(false);

    const fetchInquiries = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = statusFilter !== 'all' ? { status: statusFilter } : {};
            const response = await api.get('/inquiries', { params });
            setInquiries(response.data.data || response.data);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch inquiries',
                type: 'error'
            }));
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter, dispatch]);

    useEffect(() => {
        fetchInquiries();
    }, [fetchInquiries]);

    const handleMarkContacted = async (inquiryId: string) => {
        try {
            await api.put(`/inquiries/${inquiryId}/contact`, {
                notes: contactNotes
            });

            dispatch(showToast({
                message: 'Inquiry marked as contacted successfully!',
                type: 'success'
            }));

            setShowContactForm(false);
            setContactNotes('');
            fetchInquiries();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to mark as contacted',
                type: 'error'
            }));
        }
    };


    const handleRejectInquiry = async (inquiryId: string, reason: string) => {
        try {
            await api.put(`/inquiries/${inquiryId}/reject`, {
                reason
            });

            dispatch(showToast({
                message: 'Inquiry rejected successfully!',
                type: 'success'
            }));

            fetchInquiries();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to reject inquiry',
                type: 'error'
            }));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'contacted': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'in-progress': return 'bg-purple-50 text-purple-700 border-purple-200';
            case 'approved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <ClockIcon className="w-4 h-4" />;
            case 'contacted': return <ChatBubbleLeftRightIcon className="w-4 h-4" />;
            case 'in-progress': return <ClipboardDocumentListIcon className="w-4 h-4" />;
            case 'approved': return <CheckCircleIcon className="w-4 h-4" />;
            case 'rejected': return <XCircleIcon className="w-4 h-4" />;
            default: return <ClockIcon className="w-4 h-4" />;
        }
    };

    const filteredInquiries = inquiries.filter(inquiry =>
        (inquiry.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (inquiry.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (inquiry.message?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );


    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading inquiries...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                Company Inquiry Management
                            </h1>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">
                                Manage and track company inquiries from potential clients
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or message..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="sm:w-48">
                            <div className="relative">
                                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
                                >
                                    <option value="all">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Inquiries List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Company Inquiries ({filteredInquiries.length})
                            </h2>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredInquiries.length === 0 ? (
                            <div className="p-8 text-center">
                                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No inquiries found</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {searchTerm ? 'Try adjusting your search criteria' : 'No inquiries match the current filter'}
                                </p>
                            </div>
                        ) : (
                            filteredInquiries.map((inquiry) => (
                                <div
                                    key={inquiry._id}
                                    className={`p-6 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedInquiry?._id === inquiry._id
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                                        : ''
                                        }`}
                                    onClick={() => {
                                        setSelectedInquiry(inquiry);
                                        setShowDetails(true);
                                    }}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0">
                                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                                        <UserIcon className="w-6 h-6 text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                                                        {inquiry.name || 'N/A'}
                                                    </h3>
                                                    <div className="flex items-center space-x-4 mt-1">
                                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                            <EnvelopeIcon className="w-4 h-4 mr-1" />
                                                            {inquiry.email || 'N/A'}
                                                        </div>
                                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                            <PhoneIcon className="w-4 h-4 mr-1" />
                                                            {inquiry.phone || 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <div className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                                                    <ClipboardDocumentListIcon className="w-4 h-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <span className="font-medium">Requirement:</span>
                                                        <span className="ml-1 line-clamp-2">{inquiry.message || 'No requirement specified'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-end space-y-2">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(inquiry.status)}`}>
                                                {getStatusIcon(inquiry.status)}
                                                <span className="ml-1 capitalize">{inquiry.status}</span>
                                            </span>
                                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                <CalendarIcon className="w-3 h-3 mr-1" />
                                                {inquiry.createdAt ? new Date(inquiry.createdAt).toLocaleDateString() : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>

            {/* Inquiry Details Modal */}
            {showDetails && selectedInquiry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Inquiry Details
                                </h2>
                                <button
                                    onClick={() => setShowDetails(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Company Info */}
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                        <UserIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {selectedInquiry.name || 'N/A'}
                                        </h3>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedInquiry.status)}`}>
                                            {getStatusIcon(selectedInquiry.status)}
                                            <span className="ml-1 capitalize">{selectedInquiry.status}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Full Name
                                    </label>
                                    <div className="flex items-center text-gray-900 dark:text-white">
                                        <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                                        {selectedInquiry.name || 'N/A'}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Work Email
                                    </label>
                                    <div className="flex items-center text-gray-900 dark:text-white">
                                        <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                                        {selectedInquiry.email || 'N/A'}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Phone
                                    </label>
                                    <div className="flex items-center text-gray-900 dark:text-white">
                                        <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                                        {selectedInquiry.phone || 'N/A'}
                                    </div>
                                </div>
                            </div>

                            {/* Requirement Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Requirement Description
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                                        {selectedInquiry.message || 'No requirement specified'}
                                    </p>
                                </div>
                            </div>

                            {/* Admin Notes */}
                            {selectedInquiry.adminNotes && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Admin Notes
                                    </label>
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                                            {selectedInquiry.adminNotes}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {selectedInquiry.status === 'pending' && (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setShowDetails(false);
                                                    setShowContactForm(true);
                                                }}
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                                            >
                                                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                                                Mark as Contacted
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const reason = prompt('Enter rejection reason:');
                                                    if (reason) {
                                                        handleRejectInquiry(selectedInquiry._id, reason);
                                                        setShowDetails(false);
                                                    }
                                                }}
                                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
                                            >
                                                <XCircleIcon className="w-4 h-4 mr-2" />
                                                Reject Inquiry
                                            </button>
                                        </>
                                    )}

                                    {selectedInquiry.status === 'contacted' && (
                                        <button
                                            onClick={() => {
                                                const reason = prompt('Enter rejection reason:');
                                                if (reason) {
                                                    handleRejectInquiry(selectedInquiry._id, reason);
                                                    setShowDetails(false);
                                                }
                                            }}
                                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
                                        >
                                            <XCircleIcon className="w-4 h-4 mr-2" />
                                            Reject Inquiry
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Form Modal */}
            {showContactForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Mark as Contacted
                                </h3>
                                <button
                                    onClick={() => setShowContactForm(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label htmlFor="contactNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Contact Notes
                                </label>
                                <textarea
                                    id="contactNotes"
                                    value={contactNotes}
                                    onChange={(e) => setContactNotes(e.target.value)}
                                    rows={4}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Add notes about the contact..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    onClick={() => setShowContactForm(false)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleMarkContacted(selectedInquiry!._id)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                                >
                                    <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                                    Mark Contacted
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCompanyInquiryManagement;
